import React, { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'

import { ZinButton, ZinIconButton } from '@tnl/zin-react'
import { useScrollLock } from '@hooks/useScrollLock'
import { useSiteContext } from '@contexts/MkSiteContext'
import './Dialog.scss'

interface DialogProps {
  appendTo?: HTMLElement
  visible?: boolean
  onClose?: () => void
  children: React.ReactNode
  className?: string
  modal?: boolean
  /** If true, scroll window to top when dialog closes/unlocks. */
  scrollToTopOnClose?: boolean
  title?: string
  subTitle?: string
  /** Optional content rendered in a full-width row below the title/close row, still inside the sticky header. */
  headerContent?: React.ReactNode
  widthAuto?: boolean
  headerAction?: {
    label: string
    onClick: () => void
    icon?: string
    variant?: 'primary' | 'link' | 'secondary'
  }
  primaryAction?: {
    label: string
    onClick: () => void
    disabled?: boolean
    icon?: string
  }
  secondaryAction?: {
    label: string
    onClick: () => void
    disabled?: boolean
  }
  size?: 'small' | 'medium' | 'large'
  fullWidthCTA?: boolean
}

export default function Dialog({
  appendTo,
  visible = false,
  onClose,
  children,
  className,
  modal = true,
  scrollToTopOnClose = false,
  title,
  subTitle,
  headerContent,
  widthAuto = false,
  headerAction,
  primaryAction,
  secondaryAction,
  fullWidthCTA = false,
  size = 'medium',
}: DialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [isClosing, setIsClosing] = useState(false)
  const { iconSet } = useSiteContext()
  const titleId = useId()

  // Lock scroll when dialog is open
  useScrollLock(visible, [], scrollToTopOnClose)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    if (visible) {
      setIsClosing(false)
      if (modal) {
        dialog.showModal()
      } else {
        dialog.show()
      }

      // Keep initial focus on the dialog container so the close button
      // does not show a focused state immediately on open.
      requestAnimationFrame(() => {
        dialog.focus({ preventScroll: true })
      })
    } else {
      setIsClosing(true)
      // Add a delay before actually closing the dialog to allow for animations
      const closeTimeout = setTimeout(() => {
        dialog.close()
        setIsClosing(false)
      }, 300) // Match this with your CSS transition duration

      return () => clearTimeout(closeTimeout)
    }

    return () => {
      // Cleanup handled by useScrollLock hook
    }
  }, [visible, modal])

  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (onClose && e.target === e.currentTarget) {
      onClose()
    }
  }

  const dialogContent = (
    <dialog
      ref={dialogRef}
      tabIndex={-1}
      aria-labelledby={title ? titleId : undefined}
      className={clsx(
        'dialog-new',
        visible && !isClosing,
        widthAuto && 'dialog-new--width-auto',
        {
          'dialog-new--small': size === 'small',
          'dialog-new--medium': size === 'medium',
          'dialog-new--large': size === 'large',
        },
        className,
      )}
      onClick={handleBackdropClick}>
      <div className="dialog-new__inner">
        <div className="dialog-new__header">
          <div className="dialog-new__header-top-row">
            <div className="dialog-new__header-action">
              {headerAction && (
                <>
                  <ZinIconButton
                    aria-label={headerAction.label}
                    className="dialog-new__header-action--mobile text-primary"
                    icon={headerAction.icon || 'calendar'}
                    onClick={headerAction.onClick}
                    set={iconSet}
                  />
                  <ZinButton
                    className="dialog-new__header-action--desktop text-primary"
                    icon={headerAction.icon || 'calendar'}
                    variant={headerAction.variant || 'primary'}
                    onClick={headerAction.onClick}>
                    {headerAction.label}
                  </ZinButton>
                </>
              )}
            </div>
            <div className="dialog-new__title">
              {title && (
                <h2
                  className="uppercase modal-title-2"
                  id={titleId}>
                  {title}
                </h2>
              )}
              {subTitle && <div className="dialog-new__sub-title body-1--semibold">{subTitle}</div>}
            </div>
            <div className="dialog-new__close">
              <ZinIconButton
                title="Close"
                className="dialog-new__close-button"
                icon="xmark"
                onClick={onClose}
                set={iconSet}
              />
            </div>
          </div>
          {headerContent && <div className="dialog-new__header-content">{headerContent}</div>}
        </div>
        <div className="dialog-new__content">{children}</div>

        {(primaryAction || secondaryAction) && (
          <div className="dialog-new__footer uppercase">
            {secondaryAction && (
              <ZinButton
                variant="secondary"
                onClick={secondaryAction.onClick}
                className={clsx('dialog-new__footer-button', fullWidthCTA && 'w-full')}
                size="medium"
                disabled={secondaryAction.disabled}>
                {secondaryAction.label}
              </ZinButton>
            )}
            {primaryAction && (
              <ZinButton
                variant="primary"
                icon={primaryAction.icon}
                onClick={primaryAction.onClick}
                className={clsx('dialog-new__footer-button', fullWidthCTA && 'w-full')}
                full={fullWidthCTA}
                size="medium"
                disabled={primaryAction.disabled}>
                {primaryAction.label}
              </ZinButton>
            )}
          </div>
        )}
      </div>
    </dialog>
  )

  if (appendTo) {
    return createPortal(dialogContent, appendTo)
  }

  return dialogContent
}
